import type { Action } from "@judo/actions";
import {
	useDispatchOptional,
	useEditMode,
	useElementDisabled,
	useElementVisibility,
	usePageContextOptional,
} from "@judo/core";
import { useModelLabel } from "@judo/i18n";
import type { Button as ButtonElement, ButtonGroup as ButtonGroupElement, PageDefinition } from "@judo/model-api";
import { getButtonGroupTestId, getButtonTestId } from "@judo/test-ids";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Button, ButtonGroup, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { useState } from "react";
import { findActionByDefinition } from "../utils/find-action-by-definition";
import { getVisibleButtons } from "../utils/get-visible-buttons";
import { IconRenderer } from "./IconRenderer";

export interface InlineButtonGroupRendererProps {
	element: ButtonGroupElement;
	/** Optional page override (uses context if not provided) */
	page?: PageDefinition;
	editMode?: boolean;
	/** Optional dispatch override (uses context if not provided) */
	onDispatch?: (action: Action) => Promise<unknown>;
}

/**
 * Renderer for inline ButtonGroup elements (within forms/views).
 *
 * Supports `featuredActions` — when set to a number > 0, that many buttons
 * are "extracted" from the dropdown and rendered as inline buttons to the left
 * of the dropdown trigger. Featured buttons and the dropdown are rendered
 * within a single MUI ButtonGroup.
 *
 * When featuredActions is 0 (default), all buttons appear inside a dropdown menu.
 */
export function InlineButtonGroupRenderer({
	element,
	page: pageProp,
	editMode,
	onDispatch: onDispatchProp,
}: InlineButtonGroupRendererProps) {
	// Check group-level visibility (hiddenBy dynamic data, static hidden, customization overrides)
	const groupHidden = useElementVisibility(element);

	// Use props if provided, otherwise fall back to context
	const pageContext = usePageContextOptional();
	const dispatchContext = useDispatchOptional();
	const { isEditMode: derivedEditMode } = useEditMode();

	const page = pageProp ?? pageContext?.pageDefinition;
	const onDispatch = onDispatchProp ?? dispatchContext?.dispatch;

	// Resolve edit mode: use prop if provided, otherwise derive from hook.
	// When in edit mode, inline buttons are disabled (not hidden).
	const resolvedEditMode = editMode ?? derivedEditMode;

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const handleDropdownClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const dispatchButtonAction = (button: ButtonElement) => {
		const action = page ? findActionByDefinition(page, button.actionDefinition) : undefined;

		if (action && onDispatch) {
			onDispatch(action as Action).catch((error) => {
				console.error("Button action failed:", error);
			});
		}
	};

	const handleButtonClick = (button: ButtonElement) => {
		dispatchButtonAction(button);
	};

	const handleMenuItemClick = (button: ButtonElement) => {
		handleClose();
		dispatchButtonAction(button);
	};

	// Filter buttons to only show those with matching page actions.
	// Inline button groups always keep non-edit buttons visible — edit mode
	// controls disabling (greyed out) rather than visibility.
	const visibleButtons = getVisibleButtons(element.buttons, page?.actions);

	// Don't render empty button group
	if (groupHidden || visibleButtons.length === 0) {
		return null;
	}

	const groupTestId = getButtonGroupTestId(element);
	const { label: dropdownLabel } = useModelLabel(element);
	const featuredCount = element.featuredActions ?? 0;

	// Split into featured (inline buttons) and overflow (dropdown menu items)
	const featuredButtons = featuredCount > 0 ? visibleButtons.slice(0, featuredCount) : [];
	const overflowButtons = featuredCount > 0 ? visibleButtons.slice(featuredCount) : visibleButtons;

	return (
		<>
			<ButtonGroup data-testid={groupTestId}>
				{featuredButtons.map((button) => (
					<InlineFeaturedButton
						key={button["xmi:id"]}
						button={button}
						onClick={() => handleButtonClick(button)}
						disabled={button.disabled === true || resolvedEditMode === true}
					/>
				))}
				{overflowButtons.length > 0 && (
					<Button
						variant="outlined"
						onClick={handleDropdownClick}
						disabled={resolvedEditMode === true}
						data-testid={`${groupTestId}::dropdown`}
						startIcon={element.icon ? <IconRenderer icon={element.icon} /> : undefined}
						endIcon={<KeyboardArrowDownIcon />}
						aria-controls={open ? `${groupTestId}-menu` : undefined}
						aria-haspopup="true"
						aria-expanded={open ? "true" : undefined}
					>
						{dropdownLabel}
					</Button>
				)}
			</ButtonGroup>
			{overflowButtons.length > 0 && (
				<Menu
					id={`${groupTestId}-menu`}
					anchorEl={anchorEl}
					open={open}
					onClose={handleClose}
					MenuListProps={{
						"aria-labelledby": `${groupTestId}::dropdown`,
					}}
				>
					{overflowButtons.map((button) => (
						<InlineOverflowItem
							key={button["xmi:id"]}
							button={button}
							onClick={() => handleMenuItemClick(button)}
							disabled={button.disabled === true || resolvedEditMode === true}
						/>
					))}
				</Menu>
			)}
		</>
	);
}

/** Featured inline button with translated label. Handles hiddenBy and enabledBy. */
function InlineFeaturedButton({
	button,
	onClick,
	disabled,
}: {
	button: ButtonElement;
	onClick: () => void;
	disabled: boolean;
}) {
	const hidden = useElementVisibility(button);
	const dynamicDisabled = useElementDisabled(button);
	const { label } = useModelLabel(button);
	const buttonTestId = getButtonTestId(button);

	if (hidden) {
		return null;
	}

	return (
		<Button
			variant="outlined"
			onClick={onClick}
			disabled={disabled || dynamicDisabled}
			data-testid={buttonTestId}
			startIcon={button.icon ? <IconRenderer icon={button.icon} /> : undefined}
		>
			{label}
		</Button>
	);
}

/** Overflow menu item with translated label. Handles hiddenBy and enabledBy. */
function InlineOverflowItem({
	button,
	onClick,
	disabled,
}: {
	button: ButtonElement;
	onClick: () => void;
	disabled: boolean;
}) {
	const hidden = useElementVisibility(button);
	const dynamicDisabled = useElementDisabled(button);
	const { label } = useModelLabel(button);
	const buttonTestId = getButtonTestId(button);

	if (hidden) {
		return null;
	}

	return (
		<MenuItem onClick={onClick} disabled={disabled || dynamicDisabled} data-testid={buttonTestId}>
			{button.icon && (
				<ListItemIcon>
					<IconRenderer icon={button.icon} />
				</ListItemIcon>
			)}
			<ListItemText>{label}</ListItemText>
		</MenuItem>
	);
}
